import React from 'react';
import {
  Button,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export type ComboRow = {
  rid?: number;
  shc: string;
  nam: string;
  [key: string]: any;
};

type Props = {
  cycleCode: string;
  costCenterCode: string;
  axisCode: string;
  globalObjectiveCode: string;
  specificObjectiveCode: string;

  cycles: ComboRow[];
  costCenters: ComboRow[];
  axes: ComboRow[];
  globalObjectives: ComboRow[];
  specificObjectives: ComboRow[];

  onCycleChange: (code: string) => void;
  onCostCenterChange: (code: string) => void;
  onAxisChange: (code: string) => void;
  onGlobalObjectiveChange: (code: string) => void;
  onSpecificObjectiveChange: (code: string) => void;
  onRefresh: () => void;
  onClear: () => void;

  loading?: boolean;
};

const label = (x: ComboRow) => `${x.shc} - ${x.nam}`;

class ActivityPlanningFilterBar extends React.Component<Props> {
  render() {
    const {
      cycleCode,
      costCenterCode,
      axisCode,
      globalObjectiveCode,
      specificObjectiveCode,
      cycles,
      costCenters,
      axes,
      globalObjectives,
      specificObjectives,
      loading,
    } = this.props;

    return (
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <BoxTitle />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={this.props.onClear} disabled={loading}>Clear</Button>
            <Button variant="contained" onClick={this.props.onRefresh} disabled={loading}>Refresh Tree</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              select
              size="small"
              label="Cycle"
              value={cycleCode}
              onChange={(e) => this.props.onCycleChange(e.target.value)}
              fullWidth
            >
              <MenuItem value="">All</MenuItem>
              {cycles.map((x) => (
                <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              select
              size="small"
              label="Cost Center"
              value={costCenterCode}
              onChange={(e) => this.props.onCostCenterChange(e.target.value)}
              fullWidth
            >
              <MenuItem value="">All</MenuItem>
              {costCenters.map((x) => (
                <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              select
              size="small"
              label="Axis"
              value={axisCode}
              onChange={(e) => this.props.onAxisChange(e.target.value)}
              fullWidth
            >
              <MenuItem value="">Select Axis</MenuItem>
              {axes.map((x) => (
                <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              size="small"
              label="Global Objective (OG)"
              value={globalObjectiveCode}
              onChange={(e) => this.props.onGlobalObjectiveChange(e.target.value)}
              disabled={!axisCode}
              fullWidth
            >
              <MenuItem value="">Select OG</MenuItem>
              {globalObjectives.map((x) => (
                <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              size="small"
              label="Specific Objective (OS)"
              value={specificObjectiveCode}
              onChange={(e) => this.props.onSpecificObjectiveChange(e.target.value)}
              disabled={!globalObjectiveCode}
              fullWidth
            >
              <MenuItem value="">Select OS</MenuItem>
              {specificObjectives.map((x) => (
                <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>
    );
  }
}

const BoxTitle = () => (
  <div>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Planning Filters</Typography>
    <Typography variant="caption" color="text.secondary">
      Select Axis → OG → OS. The selected OS feeds the Planning Tree objective filter.
    </Typography>
  </div>
);

export default ActivityPlanningFilterBar;
